import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useLocalSettingsStore } from "@/common/stores";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useTaskId } from "@/hooks/useTaskId";
import { useTaskNavigation } from "@/hooks/useTaskNavigation";
import TaskNotFoundFallback from "./TaskNotFoundFallback";
import TaskForm from "./task-form/TaskForm";
import TaskFormSkeleton from "./task-form/TaskFormSkeleton";

const min = 500;
const max = 1000;

const SheetResizeHandle = ({ onPointerDown }: { onPointerDown: React.PointerEventHandler }) => {
    return (
        <div
            className="bg-transparent w-2 h-full absolute top-0 left-0 z-50 cursor-ew-resize translate-x-[-50%]"
            onPointerDown={onPointerDown}
        ></div>
    );
};

export default function SidePanel() {
    const taskId = useTaskId();
    const { closeTask } = useTaskNavigation();
    const { sheetWidth, setSheetWidth } = useLocalSettingsStore();
    const initialPosition = Math.min(Math.max(sheetWidth, min), max);
    const isResizing = useRef(false);
    const [position, setPosition] = useState(initialPosition);
    const positionRef = useRef(initialPosition);

    // Keep track of the last taskId to render during exit animation
    const [displayTaskId, setDisplayTaskId] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (taskId) {
            setDisplayTaskId(taskId);
        } else if (displayTaskId) {
            // Start exit animation
            // Clear displayTaskId after animation completes
            const timer = setTimeout(() => {
                setDisplayTaskId(undefined);
            }, 400);
            return () => clearTimeout(timer);
        }
    }, [taskId, displayTaskId]);

    const isOpen = !!taskId;

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            // remove the focus from the active element
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            closeTask();
        }
    };

    const handlePointerMove = useCallback((e: PointerEvent) => {
        e.stopPropagation();
        e.preventDefault(); // prevent text selection

        let currentPosition = (() => {
            return document.body.offsetWidth - e.clientX;
        })();

        currentPosition = Math.min(Math.max(currentPosition, min), max);
        setPosition(currentPosition);
        positionRef.current = currentPosition;
    }, []);

    const handlePointerUp = useCallback((e: PointerEvent) => {
        e.stopPropagation();
        isResizing.current = false;

        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);

        setSheetWidth(positionRef.current);
    }, []);

    const handlePointerDown = useCallback<React.PointerEventHandler>((e) => {
        e.stopPropagation();
        e.preventDefault(); // prevent text selection

        isResizing.current = true;

        document.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", handlePointerUp);
    }, []);

    return (
        <Sheet open={isOpen} modal="trap-focus" onOpenChange={handleOpenChange} disablePointerDismissal={true}>
            <SheetContent
                className="shadow-none border-l border-neutral-100/80 focus-visible:outline-none dialog-shadow rounded-r-sm"
                showCloseButton={false}
                style={{ maxWidth: `${position}px` }}
            >
                <SheetResizeHandle onPointerDown={handlePointerDown} />
                <SheetHeader className="sr-only">
                    <SheetTitle>Task</SheetTitle>
                    <SheetDescription>Create a new task</SheetDescription>
                </SheetHeader>

                {displayTaskId && (
                    <ErrorBoundary FallbackComponent={TaskNotFoundFallback} resetKeys={[taskId]}>
                        <Suspense fallback={<TaskFormSkeleton />}>
                            <TaskForm key={displayTaskId} taskId={displayTaskId} onClose={closeTask} />
                        </Suspense>
                    </ErrorBoundary>
                )}
            </SheetContent>
        </Sheet>
    );
}
